import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { logout } from "../../auth/redux/slice";
import { ActivityStatuses } from "./constants";
import { endActivity } from "./thunks";

export interface ActivityState {
    status: string;
    startedAt: number | null;
    loading: boolean;
    error: string | null;
    selectedFormId: string | null;
    entityId: string | null;
    entityType: string | null;
    skipping: boolean;
    creatingContact: boolean;
}

const initialState: ActivityState = {
    status: ActivityStatuses.INITIAL,
    startedAt: null,
    loading: false,
    error: null,
    selectedFormId: null,
    entityId: null,
    entityType: null,
    skipping: false,
    creatingContact: false,
};

const activitySlice = createSlice({
    name: "activity",
    initialState,
    reducers: {
        newActivity: {
            reducer(
                state,
                action: PayloadAction<{
                    status: string;
                    startedAt: number;
                    selectedFormId: string | null;
                }>,
            ) {
                state.startedAt = action.payload.startedAt;
                state.status = action.payload.status;
                state.loading = false;
                state.error = null;
                state.selectedFormId = action.payload.selectedFormId;
                state.entityId = null;
                state.entityType = null;
                state.skipping = false;
                state.creatingContact = false;
            },
            prepare(selectedFormId: string | null = null) {
                return {
                    payload: {
                        status: ActivityStatuses.RUNNING,
                        startedAt: Date.now(),
                        selectedFormId,
                    },
                };
            },
        },
        updateActivity(state, action: PayloadAction<Record<string, unknown>>) {
            return { ...state, ...action.payload };
        },
        switchForm(state, action: PayloadAction<string>) {
            state.selectedFormId = action.payload;
        },
        removeForm(state) {
            state.selectedFormId = null;
        },
        processSuccess(state) {
            state.status = ActivityStatuses.PROCESSED;
        },
        startSkipping(state) {
            state.skipping = true;
            state.creatingContact = false;
        },
        cancelSkipping(state) {
            state.skipping = false;
        },
        startCreatingContact(state) {
            state.creatingContact = true;
            state.skipping = false;
        },
        cancelCreatingContact(state) {
            state.creatingContact = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(endActivity.pending, (state) => {
                state.loading = true;
            })
            .addCase(endActivity.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(endActivity.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "End activity failed";
            })
            .addCase(logout.fulfilled, () => ({
                ...initialState,
            }));
    },
});

export const {
    newActivity,
    updateActivity,
    switchForm,
    removeForm,
    processSuccess,
    startSkipping,
    cancelSkipping,
    startCreatingContact,
    cancelCreatingContact,
} = activitySlice.actions;
export const activityReducer = activitySlice.reducer;
